import { useMemo, useState } from 'react'
import { balance, BLOCS_SANS_SOURCE, construireEcritures, equilibre, indicateursCompta, resultat, tauxTVA } from '../../api/compta'
import { dateVerrou } from '../../api/cloture'
import type { DonneesOps } from '../../api/types'
import { fcfa } from '../Format'
import { Icone, type NomIcone } from '../Icone'
import type { PropsCompta } from '../compta/briques'
import { Cloture } from '../compta/Cloture'
import { Balance, Creances, GrandLivre, Journal, PlanComptable, ResultatVue, Tableau } from '../compta/Etats'
import { Factures } from '../compta/Factures'
import { Parametres } from '../compta/Parametres'
import { Tiers } from '../compta/Tiers'

/** Les onze sous-onglets de la console HTML, dans le même ordre. */
const ONGLETS = [
  { id: 'tableau', libelle: 'Tableau de bord', icone: 'chart' },
  { id: 'factures', libelle: 'Factures', icone: 'receipt' },
  { id: 'journal', libelle: 'Journal', icone: 'book' },
  { id: 'grandlivre', libelle: 'Grand-livre', icone: 'book' },
  { id: 'balance', libelle: 'Balance', icone: 'scale' },
  { id: 'resultat', libelle: 'Résultat', icone: 'chart' },
  { id: 'plan', libelle: 'Plan comptable', icone: 'grid' },
  { id: 'creances', libelle: 'Créances & PCA', icone: 'wallet' },
  { id: 'tiers', libelle: 'Tiers', icone: 'wallet' },
  { id: 'cloture', libelle: 'Clôture', icone: 'clock' },
  { id: 'params', libelle: 'Paramètres', icone: 'gear' },
] as const satisfies ReadonlyArray<{ id: string; libelle: string; icone: NomIcone }>

type IdOnglet = (typeof ONGLETS)[number]['id']

export function EcranCompta({
  donnees,
  orgId,
  surNotifier,
  surRafraichir,
}: {
  donnees: DonneesOps
  orgId: string
  surNotifier: (message: string) => void
  surRafraichir: () => Promise<void>
}) {
  const [onglet, setOnglet] = useState<IdOnglet>('tableau')

  const ecritures = useMemo(() => construireEcritures(donnees), [donnees])
  const soldes = useMemo(() => balance(ecritures, donnees.planComptable), [ecritures, donnees.planComptable])
  const compte = useMemo(() => resultat(ecritures), [ecritures])
  const controle = useMemo(() => equilibre(ecritures), [ecritures])
  const verrou = useMemo(() => dateVerrou(donnees.clotures, donnees.evenementsCloture), [donnees.clotures, donnees.evenementsCloture])
  const k = indicateursCompta(soldes, compte)
  const taux = tauxTVA(donnees.org)

  const p: PropsCompta = { donnees, orgId, ecritures, soldes, compte, verrou, surNotifier, surRafraichir }

  return (
    <>
      <div className="kpis">
        {[
          { l: 'Trésorerie disponible', v: fcfa(k.tresorerie), i: 'wallet' as NomIcone },
          { l: 'CA reconnu', v: fcfa(k.caReconnu), i: 'chart' as NomIcone },
          { l: 'Produits d’avance (487)', v: fcfa(k.produitsAvance), i: 'clock' as NomIcone },
          { l: 'Créances clients (411)', v: fcfa(k.creances), i: 'receipt' as NomIcone },
          { l: 'Résultat', v: fcfa(k.resultat), i: 'scale' as NomIcone },
        ].map((x) => (
          <div className="kpi" data-anim key={x.l}>
            <div className="kpi-ic">
              <Icone nom={x.i} />
            </div>
            <div className="kpi-label">{x.l}</div>
            <div className="kpi-val">{x.v}</div>
          </div>
        ))}
      </div>

      {/* Total débit = total crédit. S'il s'écarte, TOUT le reste est faux —
          d'où sa place en haut, avant les états. */}
      <div className={`bandeau${controle.ecart === 0 ? '' : ' erreur'}`} style={{ marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
        <Icone nom={controle.ecart === 0 ? 'check' : 'x'} />
        <span>
          <b>Contrôle</b> — débit {fcfa(controle.debit)} · crédit {fcfa(controle.credit)} ·{' '}
          {controle.ecart === 0 ? 'écart nul, la balance est équilibrée.' : <b>écart de {fcfa(controle.ecart)} — les états sont faux.</b>}{' '}
          {taux > 0 ? `TVA ${Math.round(taux * 1000) / 10} %, extraite des montants facturés.` : 'Organisation non assujettie à la TVA.'}
          {verrou && ` Exercices verrouillés jusqu’au ${verrou.split('-').reverse().join('/')}.`}
        </span>
      </div>

      {!donnees.synchro.encaissements && (
        <div className="bandeau erreur" style={{ marginBottom: 14 }}>
          Les encaissements de courses n’ont pas pu être lus (mise à jour de la base à appliquer, migration 0033) : les créances de
          particuliers ci-dessous sont SURÉVALUÉES tant que leurs paiements ne sont pas chargés. Ne vous fiez pas à ces états.
        </div>
      )}

      {(!donnees.synchro.documents || !donnees.synchro.clotures) && (
        <div className="bandeau alerte" style={{ marginBottom: 14 }}>
          Une partie des registres n’a pas pu être lue ({[!donnees.synchro.documents && 'écritures manuelles et plan de comptes', !donnees.synchro.clotures && 'clôtures'].filter(Boolean).join(', ')}). Les états
          ci-dessous les omettent : rechargez la page avant de vous y fier.
        </div>
      )}

      <details className="card" data-anim style={{ padding: '0 18px' }}>
        <summary style={{ cursor: 'pointer', padding: '14px 0', fontWeight: 700 }}>
          Périmètre de ces états — ce qui est calculé, ce qui n’a pas de source
        </summary>
        <p style={{ fontSize: 12.5, color: 'var(--txt2)', lineHeight: 1.55, margin: '0 0 12px' }}>
          Calculé à partir des <b>courses livrées</b> des particuliers (créance constatée à la livraison), de leurs{' '}
          <b>encaissements enregistrés</b> (espèces, Wave, Orange Money, autre) et des <b>frais mobile money</b> qui en
          découlent, des <b>factures émises</b> — dont les <b>factures mensuelles</b> des PME au forfait (forfait, option,
          dépassements, suppléments hors créneau, longue distance), des <b>dépenses</b> et des <b>écritures manuelles</b> —
          rien n’est simulé : aucune trésorerie sans paiement enregistré. Restent hors périmètre :
        </p>
        <div className="tbl-wrap" style={{ marginBottom: 14 }}>
          <table>
            <tbody>
              {BLOCS_SANS_SOURCE.map((b) => (
                <tr key={b.titre}>
                  <td className="t-strong" style={{ whiteSpace: 'nowrap' }}>
                    {b.titre}
                  </td>
                  <td className="t-muted" style={{ whiteSpace: 'normal' }}>
                    {b.detail}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      <div className="subtabs" role="tablist" aria-label="Comptabilité">
        {ONGLETS.map((o) => (
          <button
            key={o.id}
            role="tab"
            aria-selected={o.id === onglet}
            className={`subtab${o.id === onglet ? ' active' : ''}`}
            onClick={() => setOnglet(o.id)}
          >
            <Icone nom={o.icone} /> {o.libelle}
          </button>
        ))}
      </div>

      {onglet === 'tableau' && <Tableau {...p} />}
      {onglet === 'factures' && <Factures {...p} />}
      {onglet === 'journal' && <Journal {...p} />}
      {onglet === 'grandlivre' && <GrandLivre {...p} />}
      {onglet === 'balance' && <Balance {...p} />}
      {onglet === 'resultat' && <ResultatVue {...p} />}
      {onglet === 'plan' && <PlanComptable {...p} />}
      {onglet === 'creances' && <Creances {...p} />}
      {onglet === 'tiers' && <Tiers {...p} />}
      {onglet === 'cloture' && <Cloture {...p} />}
      {onglet === 'params' && <Parametres {...p} />}
    </>
  )
}
